// kvd
//   ESDataStore allows us to:
//     1. Maintain a read cache of ESRecord  (Parent DataStore class handles)
//     2. Maintain a write cache of JSDataRecords not saved to database
//     3. Generate Keys for new records ..?
//
//

import { DataStore, Mapper, Schema, Record } from "js-data";
import { ESRecord } from "./ESRecord";
import { ESRecordMapper } from "./ESRecordMapper";
import { IKeyGenerator, IKeyGeneratorSpecs } from "./libIF";
import { IProgressProvider } from "../PA.Core.IF.Basic";
import { ProgressProvider } from "../Basic/Progress";
import { ASync } from "../Basic/ASync";
import { jsdRelation, Relation_BelongsTo, Relation_HasMany, Relation_HasOne } from "./ESRecordResolver";
import { PAEnvironment } from "../PAEnvironment";
import axios from "axios";
import * as pako from "pako";
import * as _ from "lodash";
import * as AsyncLock from "async-lock";
import { PAKeyGeneratorClient } from "../PAKeyGeneratorClient";

let findAllIdLimit: number = 4096; // converted call to POST, limit only to prevent performance problem of large in list
let findAllInChunksChunkSize: number = 256;

let bRunFindAllUsingCacheInParallel = true;
let bRunFindAllInChunksInParallel = false;

if (PAEnvironment.isNode()) {
    //if running on the server, have to behave differently to not bottleneck the main thread when running large operations
    findAllIdLimit = 10240;
    findAllInChunksChunkSize = 10240;
    bRunFindAllUsingCacheInParallel = false;
    bRunFindAllInChunksInParallel = false;
}

//<editor-fold desc="Interfaces" >

export interface IESDataStoreLockCheck {
    key: number;
    modifiedTS: number;
    bUpdateTS: boolean;
}

export interface IESDataStoreServicePayload {
    LockChecks: {
        [model_id: string]: {
            Records: IESDataStoreLockCheck[]; //bUpdateTS is for whether that record gets its timestamp updated
        };
    };
    Record: {
        [model_id: string]: {
            inserts: any[];
            updates: any[];
            deletes: any[];
        };
    };
}

function createPayLoadObject(): IESDataStoreServicePayload {
    return { LockChecks: {}, Record: {} };
}

export interface IESDataStoreServicePayloadResponse {
    FailedLockChecks: {
        [model_id: string]: {
            Records: { key: number; modifiedTS: number; databaseModifiedTS: number }[];
        };
    };
    PassedLockChecks: {
        [model_id: string]: {
            Records: { key: number; databaseModifiedTS: number }[];
        };
    };
    Errors: string[];
}

function createPayLoadResponseObject(): IESDataStoreServicePayloadResponse {
    return { Errors: [], FailedLockChecks: {}, PassedLockChecks: {} };
}

export interface ICollectRelatedRecordsOptions {
    bIncludeHasMany?: boolean;
    bIncludeHasOne?: boolean;
    excludeRelationships?: { [id_mapper: string]: string[] };
    stopAt?: string[]; // Stop at Mappers
}

const collectRelatedRecordOptionDefaults: ICollectRelatedRecordsOptions = {
    bIncludeHasMany: true,
    bIncludeHasOne: true,
    excludeRelationships: {},
    stopAt: [],
};

export interface ESDataStoredefineMapperOptions {
    // custom properties
    keyGenerator?: IKeyGenerator;
    recCreatedDtTsFieldName?: string;
    recModifiedDtTsFieldName?: string;
    recDeletedIndicatorFieldName?: string;
    recDeletedIndicatorDeleteValue?: string;
    recManualIndicatorFieldName?: string;
    recManualIndicatorFieldValue?: string;
    recAutoTrimFields?: string[];
    recTableName?: string;
    recLogicalKeys?: string[];
    recNonParentForeignKeys?: { [columnName: string]: string };

    // js-data properties
    name?: string;
    idAttribute?: string;
    noValidate?: boolean;
    validateOnSet?: boolean;
    mapperClass?: typeof Mapper;
    onConflict?: string;
    emitRecordEvents?: boolean;
    endpoint?: string;
    recordClass?: typeof Record;
    applySchema?: boolean;
    schema?: Schema;
    track?: boolean;
    relations?: { [id: string]: any };
    debug?: boolean;
}

export interface ESDataStoreConstructorOptions {
    bDisableTracking?: boolean;

    [id: string]: any;
}

export interface IESDataStoreContextExtensionOptions {
    dctSchemaByEntitySet: { [id: string]: Schema };
    dctTableNameByEntitySet: { [id: string]: string };
    dctLogicalKeysByEntitySet: { [id: string]: string[] };
    dctNonParentForeignKeysByEntitySet: { [id: string]: { [columnName: string]: string } };
    dctMapperToKeyGenSpecs: { [mapper_name: string]: IKeyGeneratorSpecs };
    keyGen_end_point: string;

    [key: string]: any;
}

//</editor-fold>

export class ESDataStore extends DataStore {
    _SaveChangesProgressProvider: IProgressProvider;
    private _dctLock: any = new AsyncLock();

    //create dictionaries to hold the dictionary definitions in PAdbSchema; this generalizes settings to separate them from particular DBs
    private _dctSchemaByEntitySet: { [id: string]: Schema };
    private _dctTableNameByEntitySet: { [id: string]: string };
    private _dctLogicalKeysByEntitySet: { [id: string]: string[] };
    private _dctNonParentForeignKeysByEntitySet: { [id: string]: { [columnName: string]: string } };
    private _dctKeyGenSpecsByEntitySet: { [mapper_name: string]: IKeyGeneratorSpecs };
    private _keyGen_end_point: string;

    private readonly _dctESRecordMappersByName: { [id: string]: ESRecordMapper };
    private _dctKeyGenByName: { [id: string]: IKeyGenerator };
    private _arrBatchCompleteListeners: ((arg: IESDataStoreServicePayloadResponse) => void)[] = [];
    private readonly _bDisableTacking: boolean = false;
    private _payLoad: IESDataStoreServicePayload;
    private _payLoadResponse: IESDataStoreServicePayloadResponse;
    private _insertRecordsMap: { [mapper: string]: { [id: number]: any } };
    private _updateRecordsMap: { [mapper: string]: { [id: number]: any } };
    private _deleteRecordsMap: { [mapper: string]: { [id: number]: any } };

    constructor(options?: ESDataStoreConstructorOptions) {
        // change to options..options for browser, and node
        super(options);
        let opts: ESDataStoreConstructorOptions = options || {};

        this._dctESRecordMappersByName = {};
        this._dctKeyGenByName = {};
        this._arrBatchCompleteListeners = [];
        this._SaveChangesProgressProvider = new ProgressProvider("Saving Changes", this);
        this._bDisableTacking = false;
        if (opts.bDisableTracking) {
            this._bDisableTacking = true;
        }
    }

    get dctKeyGenSpecsByEntitySet(): { [mapper_name: string]: IKeyGeneratorSpecs } {
        return this._dctKeyGenSpecsByEntitySet;
    }

    protected setContextExtensionObjects(opts: IESDataStoreContextExtensionOptions) {
        this._dctSchemaByEntitySet = opts.dctSchemaByEntitySet;
        this._dctTableNameByEntitySet = opts.dctTableNameByEntitySet;
        this._dctLogicalKeysByEntitySet = opts.dctLogicalKeysByEntitySet;
        this._dctNonParentForeignKeysByEntitySet = opts.dctNonParentForeignKeysByEntitySet;
        this._dctKeyGenSpecsByEntitySet = opts.dctMapperToKeyGenSpecs;
        this._keyGen_end_point = opts.keyGen_end_point;
    }

    // Iterate over records to save... create/ update/ delete
    // On success, Prune deleted records
    // create records where BaseRecord._row_exists: false & _row_deleted: false
    // update records where BaseRecord._row_exists: true & _row_deleted: false & modified
    // delete records where BaseRecord._row_exists: true & _row_deleted: true

    // prune records where BaseRecord._row_exists: false & _row_deleted: true
    // prune records where BaseRecord._row_deleted: true

    //<editor-fold desc="Model property accessors">

    getModelIdFieldName(mapper_name: string): string {
        return this._dctESRecordMappersByName[mapper_name].IdAttribute;
    }

    getModelModifiedDtTsFieldName(mapper_name: string): string {
        return this._dctESRecordMappersByName[mapper_name].ModifiedDtTsFieldName;
    }

    getModelCreatedDtTsFieldName(mapper_name: string): string {
        return this._dctESRecordMappersByName[mapper_name].CreatedDtTsFieldName;
    }

    getModelProperties(mapper_name: string): string[] {
        return this._dctESRecordMappersByName[mapper_name].Properties;
    }

    getModelPropertyType(mapper_name: string, property_name: string): string {
        return this._dctESRecordMappersByName[mapper_name].getPropertyType(property_name);
    }

    getModelTableName(mapper_name: string): string {
        return this._dctESRecordMappersByName[mapper_name].TableName;
    }

    getModelKeyGeneratorSpec(mapper_name: string): IKeyGeneratorSpecs {
        let keyGenerator: IKeyGenerator = this._dctESRecordMappersByName[mapper_name].KeyGenerator;
        if (!keyGenerator) {
            return undefined;
        }
        return keyGenerator.KeyGenSpecs;
    }

    getModelSequenceName(mapper_name: string): string {
        let keyGenerator: IKeyGenerator = this._dctESRecordMappersByName[mapper_name].KeyGenerator;
        if (!keyGenerator) {
            return undefined;
        }
        return keyGenerator.SequenceName;
    }

    getModelLogicalKeys(mapper_name: string): string[] {
        return this._dctESRecordMappersByName[mapper_name].LogicalKeys;
    }

    getModelForeignKeys(mapper_name: string): any[] {
        return this._dctESRecordMappersByName[mapper_name].ForeignKeys;
    }

    getModelNonParentForeignKeys(mapper_name: string): { [columnName: string]: string } {
        return this._dctESRecordMappersByName[mapper_name].NonParentForeignkeys;
    }

    //</editor-fold>

    //<editor-fold desc="Save records">

    async SaveChanges(): Promise<ESDataStore> {
        //save all of the changes in the ESDataStore
        var self = this;
        this.emit("SaveChanges", "Saving Changes");
        this._SaveChangesProgressProvider.start("Saving Changes");
        // Iterate over all the mappers to create/ update/ delete records...

        try {
            for (var esName in this._dctESRecordMappersByName) {
                this._SaveChangesProgressProvider.setProgressMessage("SaveChangesProgress", "Saving " + esName);
                // For
                var aMapper = this._dctESRecordMappersByName[esName];
                var aCollection = this.getCollection(aMapper.name);

                var arrRows = aCollection.getAll();
                await self.Save(esName, arrRows, {});
            } // Next Mapper

            this._SaveChangesProgressProvider.finish();
            return this;
        } catch (e) {
            this.emit("SaveChanges", "Error Saving Changes");
            console.log("SaveChanges failed: ", e);
            this._SaveChangesProgressProvider.exception(e);
            return this; // Shit happened
        }
    } // async Save Changes

    async Save(esName: string, pSaveRecords: ESRecord[] | ESRecord, pOptions?: any): Promise<ESDataStore> {
        const self = this;
        let opts = pOptions || {};

        if (_.isEmpty(pSaveRecords)) {
            return self;
        }

        let saveRecords = _.isArray(pSaveRecords) ? pSaveRecords : [pSaveRecords];

        await this._dctLock.acquire("global", (done: any) => {
            self.BatchStart(opts);

            if (opts.bDoCheckLocks) {
                let mapper: ESRecordMapper = <ESRecordMapper>self._dctESRecordMappersByName[esName];

                if (!_.isUndefined(mapper.ModifiedDtTsFieldName)) {
                    _.forEach(saveRecords, (aRecord: ESRecord | any) => {
                        self.BatchAddLockCheck(
                            esName,
                            aRecord[mapper.idAttribute],
                            aRecord[mapper.ModifiedDtTsFieldName],
                        );
                    });
                }
            }

            this.SaveBatch(esName, saveRecords, opts)
                .then((ignore) => {
                    return self.BatchComplete(opts);
                })
                .then((ignore) => {
                    done(null);
                })
                .catch((err) => {
                    done(err, null);
                });
        });

        return self;
    }

    BatchStart(options: any = {}) {
        this._payLoad = createPayLoadObject();
        this._payLoadResponse = createPayLoadResponseObject();
        this._insertRecordsMap = {};
        this._updateRecordsMap = {};
        this._deleteRecordsMap = {};
        this._arrBatchCompleteListeners = [];
    }

    BatchAddLockCheck(esName: string, wbID: number, expectedTimeStamp: number, bUpdateTS?: boolean) {
        if (!_.isBoolean(bUpdateTS)) {
            bUpdateTS = true;
        }

        if (_.isUndefined(this._payLoad)) {
            this._payLoad = createPayLoadObject();
            this._payLoad.LockChecks = {};
        }

        let lockCheckRecord: IESDataStoreLockCheck = { key: wbID, modifiedTS: expectedTimeStamp, bUpdateTS: bUpdateTS };

        if (_.isUndefined(this._payLoad.LockChecks[esName])) {
            this._payLoad.LockChecks[esName] = { Records: [] };
        }

        this._payLoad.LockChecks[esName].Records.push(lockCheckRecord);
    }

    SaveBatch(esName: string, records: ESRecord[] | ESRecord, options: any = {}): Promise<ESDataStore> {
        const self = this;

        let mapper: ESRecordMapper = <any>self._dctESRecordMappersByName[esName];
        let idAttribute = mapper.idAttribute;
        let bSetUpdateTs: boolean = true;

        let opts = options || {};

        if ("bSetUpdateTs" in options) {
            bSetUpdateTs = options.bSetUpdateTs;
        }

        if (_.isUndefined(self._payLoad)) {
            self._payLoad = createPayLoadObject();
        }

        if (_.isUndefined(self._payLoad.Record[esName])) {
            self._payLoad.Record[esName] = { inserts: [], updates: [], deletes: [] };
            self._insertRecordsMap[esName] = {};
            self._updateRecordsMap[esName] = {};
            self._deleteRecordsMap[esName] = {};
        }

        if (!_.isArray(records)) {
            records = [records];
        }

        _.forEach(records, (aRecord: ESRecord | any) => {
            mapper.autotrimStrings(aRecord);
            mapper.autoRoundDecimals(aRecord);

            if (!aRecord._rowExists) {
                self._insertRecordsMap[esName][aRecord[idAttribute]] = aRecord;
            } else if (aRecord._rowDeleted && aRecord._rowExists) {
                //if the record has a deleted field, then mark it deleted for update, else mark for hard delete
                self._deleteRecordsMap[esName][aRecord[idAttribute]] = aRecord;
            } else if (aRecord.hasChanges()) {
                self._updateRecordsMap[esName][aRecord[idAttribute]] = aRecord;
            }
        });

        return ASync.resolve(self);
    }

    getBatchPayloadResponseErrors(): string[] {
        if (_.isUndefined(this._payLoadResponse) || _.isUndefined(this._payLoadResponse.Errors)) {
            return [];
        }

        return this._payLoadResponse.Errors;
    }

    async BatchComplete(options: any = {}): Promise<ESDataStore> {
        const self = this;

        try {
            self._payLoadResponse = await self.postPayloadToDataStoreServices(options);
            let arrErrors = self._payLoadResponse.Errors;
            if (!_.isEmpty(arrErrors)) {
                throw arrErrors;
            }
            self.updateRecordsAfterBatchSave(options);
            self._payLoad = createPayLoadObject();

            _.forEach(self._arrBatchCompleteListeners, (cb) => {
                cb(self._payLoadResponse);
            });

            self._arrBatchCompleteListeners = [];

            return self;
        } catch (e) {
            self._payLoad = createPayLoadObject();
            if (_.isArray(e)) {
                _.forEach(e, (anError) => {
                    self._payLoadResponse.Errors.push("Error in Batch Complete: " + anError);
                });
            } else {
                self._payLoadResponse.Errors.push("Error in Batch Complete: " + e.toString());
            }
            throw new Error(JSON.stringify(self._payLoadResponse.Errors));
        }
    }

    addBatchCompleteListener(callback: (arg: IESDataStoreServicePayloadResponse) => void): void {
        if (_.isFunction(callback)) {
            this._arrBatchCompleteListeners.push(callback);
        }
    }

    // Add record to Container, create Key ?
    createRecord(name: string, props?: any, opts?: any): Record {
        var returnValue = super.createRecord(name, props, opts);

        if (returnValue instanceof ESRecord) {
            returnValue._rowExists = false;
            returnValue._rowDeleted = false;
            returnValue.setColumnDefaults();
            returnValue.setManualIndicator();
        }

        return returnValue;
    }

    async createRecordWithKey(name: string, props?: any, opts?: any): Promise<Record> {
        const self = this;

        const newRecord = self.createRecord(name, props);
        // Generate Key if Key Generator is available
        if (self._dctESRecordMappersByName[name].KeyGenerator) {
            let anId = await self.getNewId(name);
            (<any>newRecord)[self.getModelIdFieldName(name)] = anId;
        } else {
            // could throw an error, as the IKeyGenerator wasn't available..
            // key could be set in afterCreateRecordWithKey ue.. instead..
        }

        if ("afterCreateRecordWithKey" in newRecord) {
            let afterFunction: (pr: any, o: any) => Promise<void> = (<any>newRecord)["afterCreateRecordWithKey"];
            if (_.isFunction(afterFunction)) {
                await afterFunction.call(newRecord, props, opts);
            }
        }

        return newRecord;
    }

    private updateRecordsAfterBatchSave(options: any = {}) {
        const self = this;

        if (!_.isEmpty(self._payLoadResponse.Errors)) {
            return;
        }

        if (!_.isEmpty(self._payLoadResponse.FailedLockChecks)) {
            self._payLoadResponse.Errors.push(
                "Failed locked checks during bulk save: " + JSON.stringify(self._payLoadResponse.FailedLockChecks),
            );
            return;
        }

        let responseRecord: { key: number; databaseModifiedTS: number };
        let mapper: ESRecordMapper;
        let payLoadResponseRecords: { key: number; databaseModifiedTS: number }[];
        let payLoadResponseRecordsMapByKeyId: { [key: number]: { key: number; databaseModifiedTS: number } };
        let lockCheckRecords: IESDataStoreLockCheck[];
        let lockCheckRecordMap: { [key: number]: IESDataStoreLockCheck };
        let lockCheckRecord: IESDataStoreLockCheck;
        let esRecord: ESRecord;
        let bDoUpdateTimeStamps: boolean;

        _.forEach(_.keys(self._payLoad.Record), (model_id) => {
            mapper = <ESRecordMapper>self._dctESRecordMappersByName[model_id];
            let bHasDeleteIndicator = !_.isEmpty(mapper.DeletedIndicatorFieldName);
            let idAttribute = mapper.idAttribute;
            bDoUpdateTimeStamps =
                (!_.isUndefined(mapper.CreatedDtTsFieldName) && !_.isEmpty(mapper.CreatedDtTsFieldName)) ||
                (!_.isUndefined(mapper.ModifiedDtTsFieldName) && !_.isEmpty(mapper.ModifiedDtTsFieldName));

            //each record that needs a timestamp update should exist in payLoadResponseRecords. key this by idAttribute to avoid array filtering. if the timestamp fields don't exist, don't bother
            if (bDoUpdateTimeStamps) {
                payLoadResponseRecords = _.isUndefined(self._payLoadResponse.PassedLockChecks[model_id])
                    ? []
                    : self._payLoadResponse.PassedLockChecks[model_id].Records;
                payLoadResponseRecordsMapByKeyId = _.keyBy(payLoadResponseRecords, (aRecord) => {
                    return aRecord.key;
                });
                lockCheckRecords = _.isUndefined(self._payLoad.LockChecks[model_id])
                    ? []
                    : self._payLoad.LockChecks[model_id].Records || [];
                lockCheckRecordMap = _.keyBy(lockCheckRecords, (aRec) => {
                    return aRec.key;
                });
            }

            _.forEach(self._payLoad.Record[model_id].inserts, (payLoadRecord: any) => {
                if (bDoUpdateTimeStamps) {
                    responseRecord = payLoadResponseRecordsMapByKeyId[payLoadRecord[idAttribute]];
                    if (!_.isUndefined(responseRecord)) {
                        lockCheckRecord = lockCheckRecordMap[payLoadRecord[idAttribute]];
                        //only update the timestamp for a record if the lockCheck record says to
                        if (!_.isUndefined(lockCheckRecord) && lockCheckRecord.bUpdateTS) {
                            if (!_.isUndefined(payLoadRecord[mapper.ModifiedDtTsFieldName])) {
                                payLoadRecord[mapper.ModifiedDtTsFieldName] = responseRecord.databaseModifiedTS;
                            }

                            if (!_.isUndefined(payLoadRecord[mapper.CreatedDtTsFieldName])) {
                                payLoadRecord[mapper.CreatedDtTsFieldName] = responseRecord.databaseModifiedTS;
                            }
                        }
                    }
                }

                esRecord = payLoadRecord as ESRecord;
                esRecord._rowExists = true;
                esRecord.commit();
            });

            _.forEach(self._payLoad.Record[model_id].deletes, (payLoadRecord: ESRecord) => {
                payLoadRecord.commit();
                if (!bHasDeleteIndicator) payLoadRecord._rowExists = false;
            });

            _.forEach(self._payLoad.Record[model_id].updates, (payLoadRecord: any) => {
                if (bDoUpdateTimeStamps) {
                    responseRecord = payLoadResponseRecordsMapByKeyId[payLoadRecord[idAttribute]];
                    if (!_.isUndefined(responseRecord)) {
                        lockCheckRecord = lockCheckRecordMap[payLoadRecord[idAttribute]];
                        if (!_.isUndefined(lockCheckRecord) && lockCheckRecord.bUpdateTS) {
                            payLoadRecord[mapper.ModifiedDtTsFieldName] = responseRecord.databaseModifiedTS;
                        }
                    }
                }

                esRecord = payLoadRecord as ESRecord;
                esRecord.commit();
            });
        });
    }

    private postPayloadToDataStoreServices(options: any = {}): Promise<IESDataStoreServicePayloadResponse> {
        const self = this;

        return new Promise<IESDataStoreServicePayloadResponse>((resolve, error) => {
            _.forEach(self._payLoad.Record, (aValue, aKey) => {
                aValue.deletes = _.values(self._deleteRecordsMap[aKey]);
                aValue.updates = _.values(self._updateRecordsMap[aKey]);
                aValue.inserts = _.values(self._insertRecordsMap[aKey]);

                if (
                    _.isEmpty(self._payLoad.Record[aKey].deletes) &&
                    _.isEmpty(self._payLoad.Record[aKey].inserts) &&
                    _.isEmpty(self._payLoad.Record[aKey].updates)
                ) {
                    //if there are no records on which to perform DB actions, remove the tables from the records property
                    delete self._payLoad.Record[aKey];
                }
            });

            if (_.isEmpty(self._payLoad.Record)) {
                let response: IESDataStoreServicePayloadResponse = createPayLoadResponseObject();
                resolve(response);
                return;
            }

            let sEndPoint = <string>PAEnvironment.getValue(PAEnvironment.Network_URL_Base, "/");
            if (sEndPoint.endsWith("/")) sEndPoint += "bulkSaveService";
            else sEndPoint += "/bulkSaveService";

            let data: any = {};
            data.headers = {};

            let sPayLoad = JSON.stringify(self._payLoad);
            if (sPayLoad.length > 1024) {
                data.request = pako.deflate(sPayLoad);
                data.headers["Content-Encoding"] = "gzip";
            } else {
                data.request = self._payLoad;
            }

            axios
                .post(sEndPoint, { data: data })
                .then((response: any) => {
                    let responseData = response.data as IESDataStoreServicePayloadResponse;
                    resolve(responseData);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    //</editor-fold>

    //<editor-fold desc="Create records">

    async getNewId(mapper_name: string, opts?: any): Promise<number | string> {
        let returnArray = await this.getNewIds(mapper_name, 1, opts);
        return returnArray[0];
    }

    async getNewIds(mapper_name: string, keyCount: number, opts?: any): Promise<number[] | string[]> {
        if (keyCount == 0) {
            return [];
        }
        //each model has its own key generator. Find it and call its method to get IDs
        let keyGen: IKeyGenerator = this._dctESRecordMappersByName[mapper_name].KeyGenerator;
        if (!keyGen) {
            return undefined;
        }
        return await keyGen.getNewIds(keyCount, opts);
    }

    async findAllInChunks(
        name: string,
        inQquery: any,
        fkFieldName: string,
        arrIds: (string | number)[] | number[] | string[],
        options: any = {},
    ): Promise<Record[]> {
        const self = this;
        const aMapper = self.getMapper(name);

        if (_.isEmpty(aMapper)) {
            return [];
        }

        let query = inQquery || {};
        if (_.isEmpty(query)) {
            query = { where: {} };
        } else if (!("where" in query)) {
            query = { where: query }; // in case they send over just conditions (doesn't check for order, limit,..)
        }

        let returnValue: Record[] = [];
        let tasks: Promise<any>[] = [];

        let arrIdsToLoadcChunked = _.chunk(arrIds, findAllInChunksChunkSize);
        let idx = 0;
        for (idx = 0; idx < arrIdsToLoadcChunked.length; idx++) {
            let findIds = arrIdsToLoadcChunked[idx];
            let queryPart = _.cloneDeep(query);

            queryPart.where[fkFieldName] = { inq: findIds };
            if (findIds.length > 0) {
                let subProcess = self.findAll(name, queryPart, _.clone(options)).then((arrRecords) => {
                    returnValue.push.apply(returnValue, arrRecords);
                });

                if (bRunFindAllInChunksInParallel) tasks.push(subProcess);
                else await subProcess;
            }
        }

        if (bRunFindAllInChunksInParallel) await Promise.all(tasks);

        return returnValue;
    }

    //</editor-fold>

    //<editor-fold desc="Find records">

    async findUsingCache(name: string, arrIds: number[] | string[], options: any = {}): Promise<Record[]> {
        const self = this;

        let dctRecordById: { [id: string]: Record } = {};
        let dctIdsToLoad: { [id: string]: string | number } = {};
        const aMapper = self.getMapper(name);

        if (_.isEmpty(aMapper)) return [];

        _.forEach(arrIds, (anId: string | number) => {
            if (anId in dctRecordById) {
                return;
            }

            let aRecord = this.get(name, anId);
            if (!_.isUndefined(aRecord)) {
                dctRecordById[anId] = aRecord;
            } else {
                if (!(anId in dctIdsToLoad)) {
                    dctIdsToLoad[anId] = anId;
                }
            }
        });

        let arrIdsToLoad = _.values(dctIdsToLoad);

        // Break up large requests into smaller, requests, under a predefined limit.. 1024

        //let arrRecords: Record[] = [];
        let tasks: Promise<any>[] = [];

        let arrIdsToLoadcChunked = _.chunk(arrIdsToLoad, findAllIdLimit);

        for (let idx = 0; idx < arrIdsToLoadcChunked.length; idx++) {
            let findIds = arrIdsToLoadcChunked[idx];

            if (!findIds.length) continue;

            let query = { where: { [aMapper.idAttribute]: { inq: findIds } } };
            let subProcess = self.findAll(name, query, _.clone(options)).then((arrRecords) => {
                _.forEach(arrRecords, (aRecord: any) => {
                    let anId = <string>aRecord[aMapper.idAttribute];
                    if (!(anId in dctRecordById)) {
                        dctRecordById[anId] = aRecord;
                    }
                });
            });

            if (bRunFindAllUsingCacheInParallel) tasks.push(subProcess);
            else await subProcess;
        }

        if (bRunFindAllUsingCacheInParallel) await Promise.all(tasks);

        return _.values(dctRecordById);
    }

    //</editor-fold>

    //<editor-fold desc="Methods for instantiating implementations of ESDataStore">

    defineMapper(mapperName: string, opts?: ESDataStoredefineMapperOptions): Mapper {
        let inOpts: ESDataStoredefineMapperOptions = opts || {};

        if (this._bDisableTacking) {
            inOpts.track = false;
        }

        if (!("noValidate" in inOpts)) {
            inOpts.noValidate = true;
        }

        if (!("validateOnSet" in inOpts)) {
            inOpts.validateOnSet = false;
        }

        if (!("mapperClass" in inOpts)) {
            inOpts.mapperClass = ESRecordMapper;
        }

        if (!("onConflict" in inOpts)) {
            inOpts.onConflict = "skip";
        }

        if (!("emitRecordEvents" in inOpts)) {
            inOpts.emitRecordEvents = false;
        }

        let returnValue: Mapper = super.defineMapper(mapperName, inOpts);

        this._dctESRecordMappersByName[mapperName] = returnValue as ESRecordMapper;

        // defined Mapper foreign keys immutable foreign keys
        return returnValue;
    }

    protected getKeyGenByMapper(mapper_name: string): IKeyGenerator {
        //this method is used when instantiating mappers within an implementation of the ESDataStore, so it doesn't use the EsRecordMapper's properties since they don't exist yet
        //keep the IKeyGenerators statically cached by their associated mappers so they can be shared across ESDataStore instances
        if (!(mapper_name in this._dctKeyGenByName)) {
            let keyGenSpecs: IKeyGeneratorSpecs = this._dctKeyGenSpecsByEntitySet[mapper_name];
            if (!keyGenSpecs) {
                this._dctKeyGenByName[mapper_name] = undefined;
                return this._dctKeyGenByName[mapper_name];
            }

            this._dctKeyGenByName[mapper_name] = new PAKeyGeneratorClient({
                end_point: this._keyGen_end_point,
                keyGenSpecs: keyGenSpecs,
            });
        }

        return this._dctKeyGenByName[mapper_name];
    }

    protected getTableNameByMapper(mapper_name: string): string {
        //this method is used when instantiating mappers within an implementation of the ESDataStore, so it doesn't use the EsRecordMapper's properties since they don't exist yet
        return this._dctTableNameByEntitySet[mapper_name];
    }

    protected getLogicalKeysByMapper(mapper_name: string): string[] {
        return this._dctLogicalKeysByEntitySet[mapper_name];
    }

    protected getNonParentForeignKeysByMapper(mapper_name: string): { [columnName: string]: string } {
        return this._dctNonParentForeignKeysByEntitySet[mapper_name];
    }

    //</editor-fold>

    //<editor-fold desc="Purge cache">

    private collectRelatedRecords(
        id_mapper: string,
        aRecord: Record,
        dct: { [id_mapper: string]: { [id: number]: Record } },
        opts: ICollectRelatedRecordsOptions,
    ) {
        let mapper = this.getMapper(id_mapper);

        if (!(id_mapper in dct)) {
            dct[id_mapper] = {};
        }
        let keyValue: number = (<any>aRecord)[mapper.idAttribute];
        dct[id_mapper][keyValue] = aRecord;

        if (_.includes(opts.stopAt, id_mapper)) {
            return;
        }

        let excludedRelations: string[] = opts.excludeRelationships[id_mapper] || [];

        _.forEach(mapper.relationList, (rel: jsdRelation) => {
            switch (rel.type) {
                case Relation_HasMany:
                    if (opts.bIncludeHasMany) {
                        if (!_.includes(excludedRelations, rel.relation)) {
                            // Iterate over HasMany
                            let arrHasManyRecords = (<any>aRecord)[rel.localField] || [];
                            _.forEach(arrHasManyRecords, (aRelRow) => {
                                this.collectRelatedRecords(rel.relation, aRelRow, dct, opts);
                            });
                        }
                    }
                    break;

                case Relation_HasOne:
                    if (opts.bIncludeHasOne) {
                        if (!_.includes(excludedRelations, rel.relation)) {
                            let aHasOneRecords = (<any>aRecord)[rel.localField];
                            let arrHasOneRecords: any[] = [];
                            if (!_.isEmpty(aHasOneRecords) && !_.isArray(aHasOneRecords)) {
                                arrHasOneRecords = [aHasOneRecords];
                            }
                            _.forEach(arrHasOneRecords, (aRelRow) => {
                                this.collectRelatedRecords(rel.relation, aRelRow, dct, opts);
                            });
                        }
                    }
                    break;
            }
        });
    }

    private purgeQueryCache(id_mapper: string) {
        // @ts-ignore
        this._completedQueries[id_mapper] = {};
    }

    purgeCache(
        mapper_name: string,
        pRecord: Record | Record[] | number | number[],
        options: ICollectRelatedRecordsOptions = {},
    ): Promise<void> {
        const self = this;
        let opts: ICollectRelatedRecordsOptions = {};
        _.defaultsDeep(opts, options, collectRelatedRecordOptionDefaults);

        let arrRecords: (number | Record)[] = [];
        if (_.isArray(pRecord)) {
            arrRecords = pRecord;
        } else {
            arrRecords = [pRecord];
        }

        arrRecords = _.map(arrRecords, (aNumOrRec) => {
            switch (true) {
                case _.isNumber(aNumOrRec):
                    return self.get(mapper_name, <number>aNumOrRec);
                case _.isString(aNumOrRec):
                    return self.get(mapper_name, _.toNumber(aNumOrRec) || -1);
                default:
                    return aNumOrRec;
            }
        });

        arrRecords = _.filter(arrRecords, (a) => !_.isUndefined(a));

        if (_.isEmpty(arrRecords)) {
            self.purgeQueryCache(mapper_name);
        }

        let dctMapperRowsToPurge: { [id_mapper: string]: { [id: number]: Record } } = {};
        const mapper = self.getMapper(mapper_name);
        return new Promise<void>((resolve, error) => {
            //
            // Strategy: collect all rows, then remove from dbContext
            _.forEach(arrRecords, (aRow) => {
                self.collectRelatedRecords(mapper_name, <Record>aRow, dctMapperRowsToPurge, opts);
            });

            _.forEach(dctMapperRowsToPurge, (dctRecords, record_mapper) => {
                self.purgeQueryCache(record_mapper);
                let arrMapperRecords = _.values(dctRecords);
                _.forEach(arrMapperRecords, (record) => {
                    // turn off all record events before purge
                    record.off();
                });
                let opts = { silent: true };
                //  Add API to remove all records.. self.removeAll(record_mapper,arrMapperRecords, opts)
                let collection = self.getCollection(record_mapper);
                const records = collection.removeAll(arrMapperRecords, opts);

                // This is done in the Linked Collection.. (dupped)
                //if (mapper.relationList.length && records.length) {
                //    mapper.relationList.forEach(function (def) {
                //    def.removeLinkedRecords(mapper, records)
                //  })
                //}

                // This is done in the Simple Store
                //if (records.length) {
                //  // @ts-ignore
                //  self.removeRelated(record_mapper, records, opts)
                //}
            });

            resolve();
        });
    }

    //</editor-fold>
}
